import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LanguageSwitcher from './components/LanguageSwitcher';
import Home from './pages/Home';
import Order from './pages/Order';
import ThankYou from './pages/ThankYou';
import Pay from './pages/Pay';
import FAQ from './pages/FAQ';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import Organisations from './pages/Organisations';
import HowItWorks from './pages/HowItWorks';
import MyMaker from './pages/MyMaker';
import OrderStatus from './pages/OrderStatus';
import Join from './pages/Join';
import NotFound from './pages/NotFound';
import './App.css';
import './styles/atelier.css';

// Lazy so the admin panel lands in its own chunk - customers never download
// it. Not a security boundary: every /api/admin route checks the session
// cookie itself.
const AdminApp = lazy(() => import('./admin/AdminApp'));

function App() {
  const { pathname } = useLocation();
  // The admin panel is English-only and has its own chrome, so the floating
  // language switcher would just sit on top of it. The site pages, the order
  // flow and its thank-you page have their own NL | EN in the header (and
  // the order flow's bottom bar is where the floating one would sit).
  const hideFloatingSwitcher =
    pathname.startsWith('/admin') ||
    ['/', '/faq', '/pricing', '/contact', '/organisations', '/how-it-works', '/mymaker', '/order'].includes(pathname) ||
    pathname.startsWith('/join/') ||
    pathname.startsWith('/orders/') ||
    pathname.endsWith('/thank-you');

  return (
    <>
      {/* Mounted once here rather than inside Layout, so it's on every
          page - including the order flow, status and join pages, which
          don't use Layout (no nav/footer there). */}
      {!hideFloatingSwitcher && (
        <div className="lang-switcher-float">
          <LanguageSwitcher />
        </div>
      )}

      {/* A page that crashes shows a friendly message instead of a blank
          screen; going to another page clears it. */}
      <ErrorBoundary resetKey={pathname}>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/organisations" element={<Organisations />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
          </Route>

          <Route path="/order" element={<Order />} />
          {/* For makers, reached by the QR code with their canvas: a story of
              its own, without the site's menu and footer. */}
          <Route path="/mymaker" element={<MyMaker />} />
          <Route path="/order/:publicId/thank-you" element={<ThankYou />} />
          <Route path="/order/:publicId/pay" element={<Pay />} />

          {/* No accounts: these two links are how an order is reached at all.
              /orders/... needs the curator's token, /join/... is public. */}
          <Route path="/orders/:publicId" element={<OrderStatus />} />
          <Route path="/join/:publicId" element={<Join />} />

          {/* Everything behind the password. */}
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={null}>
                <AdminApp />
              </Suspense>
            }
          />

          {/* Any other address. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </>
  );
}

export default App;
